import { Controller } from "@hotwired/stimulus"

// The map pool on the tournament form: chips that can be added, removed,
// dragged into order, shuffled or sorted.
//
// The maps live in one comma-separated column, so every change writes the list
// back into the hidden field the form submits. Nothing about the storage
// changes — this only replaces typing "de_dust2, inferno, nuke" by hand.
export default class extends Controller {
    static targets = ["field", "list", "map", "input", "empty"]

    connect() {
        this.sync()
    }

    add() {
        // One box takes several maps at once, so a pasted list still works.
        const names = this.inputTarget.value.split(",").map((name) => name.trim()).filter(Boolean)
        if (names.length === 0) return

        names.forEach((name) => this.listTarget.appendChild(this.chip(name)))
        this.inputTarget.value = ""
        this.sync()
    }

    keydown(event) {
        if (event.key !== "Enter") return

        // Enter in a text field would submit the whole tournament form.
        event.preventDefault()
        this.add()
    }

    remove(event) {
        event.currentTarget.closest("[data-map-pool-target='map']").remove()
        this.sync()
    }

    shuffle() {
        const maps = this.mapTargets
        for (let i = maps.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            this.listTarget.appendChild(maps[j])
            maps[j] = maps[i]
        }
        this.sync()
    }

    sort() {
        this.mapTargets
            .sort((a, b) => this.nameOf(a).localeCompare(this.nameOf(b)))
            .forEach((map) => this.listTarget.appendChild(map))
        this.sync()
    }

    // --- dragging -------------------------------------------------------------

    dragStart(event) {
        this.dragged = event.currentTarget
        this.dragged.classList.add("is-dragging")
        // Firefox only starts a drag once something is on the transfer.
        event.dataTransfer.effectAllowed = "move"
        event.dataTransfer.setData("text/plain", "")
    }

    dragOver(event) {
        if (!this.dragged) return
        event.preventDefault()

        const over = event.currentTarget
        if (over === this.dragged) return

        const box = over.getBoundingClientRect()
        const after = event.clientY - box.top > box.height / 2
        over.parentNode.insertBefore(this.dragged, after ? over.nextSibling : over)
    }

    drop(event) {
        event.preventDefault()
    }

    dragEnd() {
        this.dragged?.classList.remove("is-dragging")
        this.dragged = null
        this.sync()
    }

    // --- plumbing -------------------------------------------------------------

    chip(name) {
        const template = this.mapTargets[0]
        if (template) {
            const chip = template.cloneNode(true)
            chip.querySelector(".map-pool__name").textContent = name
            return chip
        }

        // Nothing to copy from — the pool started out empty.
        const chip = document.createElement("li")
        chip.className = "map-pool__map"
        chip.draggable = true
        chip.dataset.mapPoolTarget = "map"
        chip.dataset.action = "dragstart->map-pool#dragStart dragover->map-pool#dragOver " +
                              "drop->map-pool#drop dragend->map-pool#dragEnd"
        chip.innerHTML = `<span class="map-pool__name"></span>` +
                         `<button type="button" class="map-pool__remove" data-action="map-pool#remove">&times;</button>`
        chip.querySelector(".map-pool__name").textContent = name
        return chip
    }

    nameOf(map) {
        return map.querySelector(".map-pool__name").textContent.trim()
    }

    sync() {
        const names = this.mapTargets.map((map) => this.nameOf(map))
        this.fieldTarget.value = names.join(", ")
        if (this.hasEmptyTarget) this.emptyTarget.hidden = names.length > 0
    }
}
